import { SIDEBAR_CONTAINER_ID } from "shared/ui/sidebar";

import { ACCOUNT_DETAILS_LAYOUT_ID } from "@/modules/pipeline/account-details/layout";

/**
 * GLE-8457: two empty layers of the open contact card cover the lead table.
 * They are the Sidebar overlay and the empty part of the card layout. A
 * click on them closed the card, so a caller could not click another lead.
 *
 * The lead table marks each row and the row's card opener with these data
 * attributes. A click on an empty layer above a row opens that row's lead.
 */
export const LEAD_ROW_SELECTOR = "[data-lead-row]";
export const LEAD_ROW_CARD_OPENER_SELECTOR = "[data-lead-row-card-opener]";

const isEmptyCardLayer = (target: Element) =>
  target.id === ACCOUNT_DETAILS_LAYOUT_ID ||
  !target.closest(`#${SIDEBAR_CONTAINER_ID}`);

/**
 * Returns the card opener of the lead row under the pointer, or null when the
 * pointer is not on an empty card layer above a lead row.
 *
 * @param cardRoot the DOM element that holds the whole card. Content that a
 *                 portal renders outside it is never an empty layer.
 */
export const findLeadRowCardOpener = (
  event: { target: EventTarget | null; clientX: number; clientY: number },
  cardRoot: HTMLElement | null
): HTMLElement | null => {
  const target = event.target;

  if (
    !cardRoot ||
    !(target instanceof Element) ||
    !cardRoot.contains(target) ||
    !isEmptyCardLayer(target) ||
    typeof document.elementsFromPoint !== "function"
  ) {
    return null;
  }

  const elementsUnderPointer = document.elementsFromPoint(
    event.clientX,
    event.clientY
  );
  const topElement = elementsUnderPointer[0];

  /**
   * NOTE a click after a drag targets the common ancestor of the press and
   *      the release, so the top element under the pointer must be an empty
   *      card layer too
   */
  if (
    !topElement ||
    !cardRoot.contains(topElement) ||
    !isEmptyCardLayer(topElement)
  ) {
    return null;
  }

  /**
   * NOTE the first element under the pointer that is not part of the card
   *      is the element the caller sees through the card
   */
  const elementBehindCard = elementsUnderPointer.find(
    (element) => !cardRoot.contains(element) && !element.contains(cardRoot)
  );

  return (
    elementBehindCard
      ?.closest(LEAD_ROW_SELECTOR)
      ?.querySelector<HTMLElement>(LEAD_ROW_CARD_OPENER_SELECTOR) ?? null
  );
};
